/**
 * Premissas da Apresentação: o padrão vem do mercado e o vendedor pode trocar.
 *
 * Decisão do Rick (16/10): o vendedor ajusta as taxas, mas toda premissa nasce
 * de uma fonte de verdade — o Banco Central (SGS) quando existe série oficial.
 * Ágio de carta contemplada não tem índice oficial; aí o padrão é a referência
 * da North (AGIO_PADRAO_PCT) e a tela diz isso.
 *
 * opcoesDoEstudo é a única montagem das opções do estudo: a caixinha do web
 * (useEstudoEditor) e a North IA (chat-ai/apresentacao.ts) chamam a mesma, para
 * as premissas e as fontes do link serem as mesmas nos dois caminhos.
 */
import type { Segmento } from './comparativo.ts';
import { AGIO_PADRAO_PCT } from './entrada.ts';
import type { OpcoesEstudo } from './estudo.ts';
import type { Indicador, Indicadores } from './indicadoresBcb.ts';
import type { CenarioResumo } from './resumoCliente.ts';
import type { Alternativa, SecaoId } from './tipos.ts';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "01/08/2026" → "ago/26". */
export const mesAno = (data: string): string => {
  const [, mes, ano] = data.split('/');
  const nome = MESES[Number(mes) - 1];
  return nome && ano ? `${nome}/${ano.slice(-2)}` : data;
};

export type ValorDeMercado = {
  valor: number;
  /** Nome curto para o botão de voltar ao padrão: "INCC", "CDI", "média BCB". */
  nome: string;
  /** Linha de fonte mostrada ao lado do campo. */
  fonte: string;
};

const doBcb = (ind: Indicadores, serie: Indicador, nome: string, descricao: string): ValorDeMercado => ({
  valor: Math.round(serie.valor * 100) / 100,
  nome,
  fonte:
    (serie.fonte ?? ind.fonte) === 'bcb'
      ? `${descricao} · Banco Central · ${mesAno(serie.data)}`
      : `${descricao} · referência de ${mesAno(serie.data)}`,
});

export const reajusteDeMercado = (ind: Indicadores, segmento: Segmento): ValorDeMercado =>
  segmento === 'imovel'
    ? doBcb(ind, ind.incc12m, 'INCC', 'INCC 12 meses')
    : doBcb(ind, ind.ipca12m, 'IPCA', 'IPCA 12 meses');

export const jurosFinanciamentoDeMercado = (ind: Indicadores, segmento: Segmento): ValorDeMercado =>
  segmento === 'imovel'
    ? doBcb(ind, ind.jurosImovelAa, 'média BCB', 'Média do financiamento imobiliário')
    : doBcb(ind, ind.jurosVeiculoAa, 'média BCB', 'Média do financiamento de veículos');

export const cdiDeMercado = (ind: Indicadores): ValorDeMercado => doBcb(ind, ind.cdiAa, 'CDI', 'CDI');

export const valorizacaoDeMercado = (ind: Indicadores): ValorDeMercado =>
  doBcb(ind, ind.ivgr12m, 'IVG-R', 'Valorização de imóveis (IVG-R) 12 meses');

/**
 * Aluguel líquido em % do valor do imóvel ao mês. Não há série oficial no BCB;
 * 0,45% a.m. (~5,5% a.a.) fica perto do rendimento de aluguel residencial que
 * os índices de mercado vêm mostrando, já descontando vacância e despesas.
 */
export const ALUGUEL_PADRAO_AM = 0.45;

export const aluguelDeMercado = (): ValorDeMercado => ({
  valor: ALUGUEL_PADRAO_AM,
  nome: 'referência',
  fonte: 'Referência North · sem índice oficial',
});

export const agioDeMercado = (): ValorDeMercado => ({
  valor: AGIO_PADRAO_PCT,
  nome: 'referência',
  fonte: 'Referência North · sem índice oficial',
});

export const FONTE_INFORMADA = 'Informado pelo especialista';

/** O que o vendedor escolheu; o que vier vazio (null/ausente) fica no valor de mercado. */
export type EscolhasEstudo = {
  segmento: Segmento;
  /** Juros do financiamento em % a.a.; null = média do BCB para o segmento. */
  jurosAa: number | null;
  mesContemplacao: number;
  agioPct: number;
  cenario: CenarioResumo;
  cdiAa?: number | null;
  reajusteAa?: number | null;
  valorizacaoAa?: number | null;
  aluguelAm?: number | null;
  alternativas?: Alternativa[];
  secoesOcultas?: SecaoId[];
};

/** Valor da premissa e a fonte que vai para o PDF: a do mercado, ou "informado" se o vendedor trocou. */
const premissa = (mercado: ValorDeMercado, escolhido: number | null | undefined) => {
  const valor = escolhido ?? mercado.valor;
  return { valor, fonte: valor === mercado.valor ? mercado.fonte : FONTE_INFORMADA };
};

/** As opções do estudo, com as fontes de cada premissa. */
export const opcoesDoEstudo = (ind: Indicadores, e: EscolhasEstudo): OpcoesEstudo => {
  const juros = jurosFinanciamentoDeMercado(ind, e.segmento);
  const cdi = premissa(cdiDeMercado(ind), e.cdiAa);
  const reajuste = premissa(reajusteDeMercado(ind, e.segmento), e.reajusteAa);
  const valorizacao = premissa(valorizacaoDeMercado(ind), e.valorizacaoAa);
  const aluguel = premissa(aluguelDeMercado(), e.aluguelAm);
  return {
    segmento: e.segmento,
    jurosAa: e.jurosAa ?? juros.valor,
    mesContemplacao: e.mesContemplacao,
    agioPct: e.agioPct,
    cdiAa: cdi.valor,
    reajusteAa: reajuste.valor,
    valorizacaoAa: valorizacao.valor,
    aluguelAm: aluguel.valor,
    cenario: e.cenario,
    fontes: {
      juros: e.jurosAa === null ? juros.fonte : FONTE_INFORMADA,
      cdi: cdi.fonte,
      reajuste: reajuste.fonte,
      agio: e.agioPct === AGIO_PADRAO_PCT ? 'Referência North · sem índice oficial' : FONTE_INFORMADA,
      valorizacao: valorizacao.fonte,
      aluguel: aluguel.fonte,
    },
    alternativas: e.alternativas ?? [],
    secoesOcultas: e.secoesOcultas ?? [],
  };
};
